import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { getTeam, removeTeamMember } from '../api/runs';
import { ApiError } from '../api/client';
import type { Team } from '../api/types';
import ErrorAlert from './ErrorAlert';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import TeamShareCodeChip from './TeamShareCodeChip';
import QrCodeDisplay from './qr/QrCodeDisplay';

interface TeamMemberDTO {
  id: string;
  display_name: string;
  is_guest: boolean;
}

interface TeamWithMembers extends Team {
  members?: TeamMemberDTO[];
}

interface TeamMembersDialogProps {
  open: boolean;
  onClose: () => void;
  orgSlug: string;
  runId: string;
  teamId: string | null;
  teamName: string;
  /** The run's join code — the first half of a team's invite link. */
  rallyeCode: string;
  /** Called after a successful removal so the caller can refresh team counts. */
  onMemberRemoved: () => void;
}

/**
 * Coordinator view of a team's member list with per-member removal (#252).
 * Removal asks for confirmation in place; removed guests are blocked from
 * re-joining the run by the backend (SOW 8.2.6 departure record).
 */
const TeamMembersDialog: React.FC<TeamMembersDialogProps> = ({
  open,
  onClose,
  orgSlug,
  runId,
  teamId,
  teamName,
  rallyeCode,
  onMemberRemoved,
}) => {
  const { t } = useTranslation('runs');
  const [members, setMembers] = useState<TeamMemberDTO[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<TeamMemberDTO | null>(null);
  const [removing, setRemoving] = useState(false);
  // The team's own invite details, revealed on request rather than sitting in
  // the dialog: handing the code on is a deliberate act, not a default view.
  const [shareCode, setShareCode] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);

  const joinUrl =
    typeof window !== 'undefined' && rallyeCode && shareCode
      ? `${window.location.origin}/join/${rallyeCode}?team=${shareCode}`
      : null;

  // No synchronous state resets here (react-hooks/set-state-in-effect, #351):
  // fresh-per-team state comes from the parent keying this component by team
  // id, and close-time cleanup happens in handleClose below.
  useEffect(() => {
    if (!open || !teamId) return;
    let cancelled = false;
    getTeam(orgSlug, runId, teamId)
      .then((data) => {
        if (!cancelled) {
          setMembers((data as TeamWithMembers).members ?? []);
          setShareCode(data.share_code ?? null);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.detail : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [open, orgSlug, runId, teamId]);

  const handleClose = () => {
    setConfirming(null);
    setError(null);
    onClose();
  };

  const handleRemove = async () => {
    if (!teamId || !confirming) return;
    setRemoving(true);
    setError(null);
    try {
      await removeTeamMember(orgSlug, runId, teamId, confirming.id);
      setMembers((prev) => prev?.filter((m) => m.id !== confirming.id) ?? null);
      setConfirming(null);
      onMemberRemoved();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setRemoving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('teamMembers.dialogTitle', { teamName })}</DialogTitle>
      <DialogContent>
        {error && <ErrorAlert message={error} />}
        {confirming ? (
          <DialogContentText>
            {t('teamMembers.removeConfirm', { name: confirming.display_name })}
          </DialogContentText>
        ) : members === null && !error ? (
          <CircularProgress size={24} />
        ) : members && members.length === 0 ? (
          <Typography color="text.secondary">{t('teamMembers.empty')}</Typography>
        ) : (
          <List dense>
            {members?.map((m) => (
              <ListItem
                key={m.id}
                disablePadding
                secondaryAction={
                  <Tooltip title={t('teamMembers.remove', { name: m.display_name })}>
                    <IconButton
                      edge="end"
                      size="small"
                      aria-label={t('teamMembers.remove', { name: m.display_name })}
                      onClick={() => setConfirming(m)}
                    >
                      <PersonRemoveIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemAvatar>
                  <Avatar sx={{ width: 32, height: 32 }}>
                    <PersonIcon fontSize="small" />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={m.display_name}
                  secondary={m.is_guest ? t('teamMembers.guest') : undefined}
                />
              </ListItem>
            ))}
          </List>
        )}

        {/* Adding someone means handing over the team's code — the person joins
            themselves, exactly as they would from a teammate's phone (#478).
            Folded away until asked for, so the dialog stays a member list. */}
        {!confirming && shareCode && (
          <Box sx={{ mt: 2 }}>
            {inviteOpen ? (
              <Stack spacing={1.5} sx={{ alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('teamMembers.invite.hint')}
                </Typography>
                <TeamShareCodeChip code={shareCode} variant="full" />
                {joinUrl && <QrCodeDisplay value={joinUrl} size={200} />}
              </Stack>
            ) : (
              <Button startIcon={<PersonAddAlt1Icon />} onClick={() => setInviteOpen(true)}>
                {t('teamMembers.invite.open')}
              </Button>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {confirming ? (
          <>
            <Button onClick={() => setConfirming(null)} disabled={removing}>
              {t('teamMembers.cancel')}
            </Button>
            <Button color="error" onClick={handleRemove} disabled={removing}>
              {t('teamMembers.confirmRemove')}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose}>{t('teamMembers.close')}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TeamMembersDialog;
